(ns ui.view.counterparty-diligence.responder-form.view
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.view.counterparty-diligence.responder-form.card :as form-card]
   [ui.view.counterparty-diligence.responder-form.entity-list-editor :as entity-list-editor]
   [ui.view.counterparty-diligence.responder-form.event :as event]
   [ui.view.counterparty-diligence.responder-form.file-input :as file-input]
   [ui.view.counterparty-diligence.responder-form.model :as model]
   [ui.view.counterparty-diligence.responder-form.nationality-list-editor :as nationality-list-editor]
   [ui.view.counterparty-diligence.responder-form.signatory-list-editor :as signatory-list-editor]
   [ui.view.counterparty-diligence.responder-form.sub :as sub]
   [ui.view.counterparty-diligence.responder-form.validation-error-text :as error-text]
   [ui.view.sheets.primitives :as sheet-primitives]))

(defn remove-entity-list-entity-confirmation-modal-body
  [{:keys [close confirm modal-props]}]
  [:div
   {:class "flex flex-col gap-2"}
   [:p (loc/tr :remove- [(-> modal-props :entity-type-label string/lower-case)])]
   [:p
    {:class "text-sm m-0 text-base-light-text-paler"}
    (loc/tr :this-action-cannot-be-undone)]
   [:div
    {:class "flex items-center gap-2 justify-end"}
    [button/sm-secondary-hollow
     {:on-click close}
     (loc/tr :cancel)]
    [button/sm-danger-solid
     {:on-click confirm}
     (loc/tr :remove)]]])

(defn discard-changes-confirmation-modal-body
  [{:keys [close confirm]}]
  [:div
   {:class "flex flex-col"}
   [:p
    {:class "text-lg font-medium"}
    (loc/tr :attention)]
   [:p
    {:class "text-base-light-text-paler mt-1"}
    (loc/tr :any-changes-you-have-made-will-be-lost)]
   [:div
    {:class "flex items-center gap-2 justify-end mt-3"}
    [button/sm-secondary-hollow
     {:on-click close}
     (loc/tr :cancel)]
    [button/sm-danger-solid
     {:on-click confirm}
     (loc/tr :continue)]]])

(defn submit-form-confirmation-modal-body
  [{:keys [state-path submitting? close]}]
  [:div
   {:class "flex flex-col gap-5"}
   [:p
    {:class "font-semibold"}
    (loc/tr :submit-form)]
   [:div
    {:class "text-sm m-0"}
    (loc/tr :the-following-pdf-document-will-be-submitted)]
   [:div
    {:class "flex flex-col gap-1"}
    [:div
     {:class    "flex justify-between shadow rounded border px-3 py-2 cursor-pointer bg-base-light hover:bg-base-regular"
      :on-click #(rf/dispatch [::event/generate-pdf-and-open-preview {:state-path state-path}])}
     [:div
      {:class "flex gap-2 items-center"}
      [:i
       {:class "fal fa-file"}]
      [:p
       {:class "text-sm m-0"}
       "answers.pdf"]]]
    [:p
     {:class "text-xs m-0 text-base-light-text-paler"}
     (loc/tr :open-document-preview-by-clicking-it)]]

   [:div
    {:class "flex items-center gap-2 justify-end"}
    [button/sm-secondary-hollow
     {:on-click  close
      :disabled? submitting?}
     (loc/tr :cancel)]
    [button/sm-primary-solid
     {:on-click  #(rf/dispatch [::event/submit-form {:state-path state-path}])
      :disabled? submitting?}
     (if submitting?
       (loc/tr :submitting)
       (loc/tr :submit))]]])

(defn remove-file-confirmation-modal-body
  [{:keys [close confirm]}]
  [:div
   {:class "flex flex-col gap-2"}
   [:p (loc/tr :remove-file?)]
   [:p
    {:class "text-sm m-0 text-base-light-text-paler"}
    (loc/tr :this-action-cannot-be-undone)]
   [:div
    {:class "flex items-center gap-2 justify-end"}
    [button/sm-secondary-hollow
     {:on-click close}
     (loc/tr :cancel)]
    [button/sm-danger-solid
     {:on-click confirm}
     (loc/tr :remove)]]])

(defn confirmation-modal
  [{:keys [state-path]}]
  (let [ctx {:state-path state-path}
        modal-state @(rf/subscribe [::sub/confirmation-modal-state ctx])
        modal-type (:modal-type modal-state)
        modal-props (:props modal-state)
        confirm-event (:confirm-event modal-state)
        submitting? (:submitting? modal-state)
        close #(rf/dispatch [::event/close-confirmation-modal ctx])
        confirm (fn []
                  (rf/dispatch confirm-event)
                  (close))]
    [:dialog
     {:id    (model/confirmation-modal-id state-path)
      :class "rounded border w-96 shadow shadow-lg open:animate-in open:slide-in-from-bottom-2 open:fade-in backdrop:animate-in backdrop:fade-in focus:outline-none"}
     (case modal-type
       :remove-entity-list-entity
       [remove-entity-list-entity-confirmation-modal-body
        {:close       close
         :confirm     confirm
         :modal-props modal-props}]

       :remove-file
       [remove-file-confirmation-modal-body
        {:close   close
         :confirm confirm}]

       :discard-changes
       [discard-changes-confirmation-modal-body
        {:close   close
         :confirm confirm}]

       :submit-form
       [submit-form-confirmation-modal-body
        {:close       close
         :submitting? submitting?
         :state-path  state-path}]

       nil)]))

(defn form-preface
  [{:keys [state-path]}]
  (let [title @(rf/subscribe [::sub/title {:state-path state-path}])
        content @(rf/subscribe [::sub/form-preface-content {:state-path state-path}])]
    [form-card/view
     [:div
      [:h1 title]]
     [:div
      {:class "mt-3"}
      [tiptap/viewer {:value content}]]]))

(defn date-input-wrapper
  []
  (let [anchor-el-ref (r/atom nil)]
    (fn [{:keys [value label read-only? on-change on-blur date-format]}]
      [:div
       [labelled-input/view
        (merge
         {:value         value
          :label         label
          :data-type     :data.type/date
          :disabled?     read-only?
          :date-format   date-format
          :placeholder   date-format
          :is-clearable? true
          :portal-root   @anchor-el-ref
          :on-change     on-change
          :on-blur       on-blur})]
       [:div
        {:class "flex justify-end"}
        [:div
         {:class "w-px"
          :ref   (fn [ref]
                   (when-not @anchor-el-ref
                     (reset! anchor-el-ref ref)))}]]])))

(defn question-view
  [{:keys [question-config section-id state-path answers-path
           lang-kw date-format read-only? attachments-path] :as props}]
  (let [{:keys [label field data-type editor-type options]} question-config
        value @(rf/subscribe [::sub/field-value {:field        field
                                                 :section-id   section-id
                                                 :state-path   state-path
                                                 :answers-path answers-path}])
        validation-errors @(rf/subscribe [::sub/field-errors {:field      field
                                                              :state-path state-path}])
        show-validation-errors? @(rf/subscribe [::sub/show-field-errors? {:field      field
                                                                          :state-path state-path}])

        select-field? (some #{data-type} [:data.type/keyword :data.type/keyword-multi])
        on-blur (fn [_e]
                  (rf/dispatch [::event/on-blur-field {:field      field
                                                       :state-path state-path}]))]
    (cond
      (= data-type :data.type/file)
      [file-input/view
       (merge props
              {:value                   value
               :field                   field
               :validation-errors       validation-errors
               :show-validation-errors? show-validation-errors?
               :add-files               (fn [files]
                                          (rf/dispatch [::event/add-files-to-field
                                                        {:files            files
                                                         :field            field
                                                         :section-id       section-id
                                                         :state-path       state-path
                                                         :attachments-path attachments-path}]))
               :open-attachment         (fn [id]
                                          (rf/dispatch [::event/open-attachment
                                                        {:attachment-id id
                                                         :state-path    state-path}]))
               :download-attachment     (fn [id]
                                          (rf/dispatch [::event/download-attachment
                                                        {:attachment-id id
                                                         :state-path    state-path}]))
               :remove-file             (fn [id]
                                          (rf/dispatch [::event/open-confirmation-modal
                                                        {:state-path    state-path
                                                         :modal-type    :remove-file
                                                         :confirm-event [::event/remove-file-from-field
                                                                         {:file-id          id
                                                                          :field            field
                                                                          :section-id       section-id
                                                                          :state-path       state-path
                                                                          :attachments-path attachments-path}]}]))})]

      (= data-type :data.type/vector)
      (case (:editor-type question-config)
        :editor.type/entity-list
        [:div
         {:class "mb-5"}
         [entity-list-editor/view
          (merge question-config
                 {:value                   value
                  :section-id              section-id
                  :state-path              state-path
                  :lang-kw                 lang-kw
                  :date-format             date-format
                  :read-only?              read-only?
                  :validation-errors       validation-errors
                  :show-validation-errors? show-validation-errors?})]]

        :editor.type/signatory-list
        [signatory-list-editor/view
         (merge props
                question-config
                {:value                   value
                 :read-only?              read-only?
                 :validation-errors       validation-errors
                 :show-validation-errors? show-validation-errors?})]

        :editor.type/nationality-list
        [:div
         {:class "mb-5 flex flex-col gap-2"}
         (when (get label lang-kw)
           [:p
            {:class "text-semibold"}
            (get label lang-kw)])
         [nationality-list-editor/view
          (merge props
                 question-config
                 {:value                   value
                  :field                   field
                  :read-only?              read-only?
                  :validation-errors       validation-errors
                  :show-validation-errors? show-validation-errors?})]])

      (= data-type :data.type/date)
      [:div
       {:class "relative flex flex-col gap-1"}
       [date-input-wrapper
        (merge
         {:value         value
          :label         label
          :disabled?     read-only?
          :on-blur       on-blur
          :is-clearable? true
          :date-format   date-format
          :on-change     (fn [value]
                           (rf/dispatch-sync [::event/change-field-value
                                              {:value      value
                                               :field      field
                                               :section-id section-id
                                               :state-path state-path}])
                           (when select-field?
                             (on-blur nil)))})]
       (when show-validation-errors?
         [error-text/view
          {:validation-errors validation-errors}])]

      :else
      [:div
       {:class "relative flex flex-col gap-1"}
       [labelled-input/view
        {:value         value
         :label         label
         :data-type     data-type
         :editor-type   editor-type
         :options       options
         :disabled?     read-only?
         :on-blur       on-blur
         :is-clearable? true
         :on-change     (fn [value]
                          (rf/dispatch-sync [::event/change-field-value
                                             {:value      value
                                              :field      field
                                              :section-id section-id
                                              :state-path state-path}])
                          (when select-field?
                            (on-blur nil)))}]
       (when show-validation-errors?
         [error-text/view
          {:validation-errors validation-errors}])])))

(defn section-view
  [{:keys [answers* section-id fields title lang-kw template
           render-form depth field-path value-path answers-path date-format
           state-path read-only? guest-access-only? attachments-path visible-fields
           info-text info-text-position] :as _args}]
  (let [questions
        (->> fields
             (mapv (fn [field]
                     (let [field-config (-> template
                                            (get-in [:template/schema field])
                                            (assoc :field field))
                           visible? (contains? visible-fields field)
                           options (some->> field-config
                                            :options
                                            (mapv (fn [opt]
                                                    (let [value (or (:value opt)
                                                                    (:key opt))
                                                          label (cond
                                                                  (-> opt :translations map?)
                                                                  (get-in opt [:translations lang-kw])

                                                                  (-> opt :label map?)
                                                                  (get-in opt [:label lang-kw])

                                                                  :else (:label opt))]
                                                      (assoc opt :value value :label label)))))]
                       (cond-> field-config
                         true (assoc :label (get-in field-config [:label lang-kw])
                                     :info-text (get-in field-config [:info-text lang-kw])
                                     :info-text-position (:info-text-position field-config)
                                     :visible? visible?)
                         (seq options) (assoc :options options)
                         (:set-as-ubo field-config)
                         (update :set-as-ubo
                                 (fn [{:keys [target-field] :as cfg}]
                                   (let [target-schema (get-in template [:template/schema target-field])]
                                     (assoc cfg
                                            :target-item-template (:item-template target-schema)
                                            :target-entity-label  (:entity-label target-schema)))))))))
             (filterv (fn [{:keys [visible?]}]
                        visible?)))
        info-text (get info-text lang-kw)
        has-info-text? (and (string? info-text)
                            (not (string/blank? info-text)))
        info-text-position (or info-text-position :top)]
    [form-card/view
     {:title title}
     [:div
      {:class "flex flex-col gap-5"}
      (when (and has-info-text?
                 (= info-text-position :top))
        [:div
         ;; HACK: Fix border and too much padding and margin on rich text viewer
         {:class "[&_.ProseMirror]:p-0 [&_.ProseMirror]:border-0 [&_.ProseMirror_*]:m-0"}
         [tiptap/viewer {:value info-text}]])

      (if (seq questions)
        (->> questions
             (map (fn [q]
                    (let [field (:field q)]
                      ^{:key (str field)}
                      [question-view
                       {:field              field
                        :lang-kw            lang-kw
                        :date-format        date-format
                        :read-only?         read-only?
                        :section-id         section-id
                        :state-path         state-path
                        :value-path         (conj value-path field)
                        :field-path         (conj field-path field)
                        :question-config    q
                        :depth              depth
                        :answers*           answers*
                        :render-form        render-form
                        :answers-path       answers-path
                        :attachments-path   attachments-path
                        :guest-access-only? guest-access-only?}]))))

        [:div {:class "text-sm m-0"} "-"])]]))

(defn view
  [{:keys [root? state-path read-only? answers-path attachments-path] :as props}]
  (let [template @(rf/subscribe [::sub/template {:state-path state-path}])
        template (if (contains? props :template)
                   (:template props)
                   template)
        lang-kw @(rf/subscribe [::sub/language {:state-path state-path}])
        lang-kw (or (:lang-kw props) lang-kw)
        date-format @(rf/subscribe [::sub/date-format {:state-path state-path}])
        visible-fields @(rf/subscribe [::sub/visible-fields {:state-path state-path}])
        e-signature-required? @(rf/subscribe [::sub/e-signature-required? {:state-path state-path}])
        guest-access-only? @(rf/subscribe [::sub/guest-access-only? {:state-path state-path}])
        answers @(rf/subscribe [::sub/answers {:state-path state-path}])
        sections (->> template
                      :template/sections
                      (mapv (fn [[id section-config]]
                              (let [title (get-in section-config [:title lang-kw])
                                    signatory-section? (:signatory-section? section-config)
                                    section-visibility-fn (:visibility-fn section-config)
                                    visibility-fn-visible? (if section-visibility-fn
                                                             (section-visibility-fn {:data answers
                                                                                     :e-signature-required? e-signature-required?})
                                                             true)
                                    visible? (and visibility-fn-visible?
                                                  (if signatory-section?
                                                    (and root? e-signature-required?)
                                                    true))]
                                (assoc section-config
                                       :id id
                                       :title title
                                       :visible? visible?))))
                      (filterv :visible?)
                      (sort-by :ord)
                      vec)]
    [:div
     {:class "grow w-full h-full overflow-hidden overflow-y-auto"}
     [:div
      {:class "flex flex-col gap-4 py-2"}
      (when root?
        [form-preface props])
      (->> sections
           (map (fn [section-config]
                  (let [section-id (:id section-config)]
                    ^{:key (str section-id)}
                    [section-view
                     (merge section-config
                            {:state-path         state-path
                             :answers-path       answers-path
                             :section-id         section-id
                             :template           template
                             :lang-kw            lang-kw
                             :date-format        date-format
                             :read-only?         read-only?
                             :attachments-path   attachments-path
                             :guest-access-only? guest-access-only?
                             :visible-fields     visible-fields})]))))]

     [confirmation-modal
      {:state-path state-path}]]))

(defn sheet-view
  [{:keys [props sheet-id sheet-state-path close-sheet-event]}]
  (let [{:keys [sheet-title read-only?]} props
        state-path sheet-state-path]
    [:<>
     [sheet-primitives/sheet-title
      {:class "px-5 py-3 font-semibold border border-x-0 border-t-0"}
      sheet-title]
     [:div
      {:class "flex flex-col grow p-5 overflow-hidden overflow-y-auto"}
      [view
       (assoc props
              :state-path state-path)]]
     [:div
      {:class "flex items-center gap-2 px-5 py-3 border border-x-0 border-b-0"}
      [button/sm-secondary-hollow
       {:on-click #(rf/dispatch [::event/dismiss-sheet-form
                                 {:state-path        state-path
                                  :close-sheet-event close-sheet-event}])}
       (loc/tr :close)]

      (when-not read-only?
        [button/sm-primary-solid
         {:on-click #(rf/dispatch [::event/submit-sheet-form
                                   {:sheet-id          sheet-id
                                    :state-path        state-path
                                    :close-sheet-event close-sheet-event}])}
         (loc/tr :save)])]]))
